import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { qk } from '@/api/queries';
import type { ConnectorLimits, ConnectorOut, ConnectorPatch } from '@/api/types';
import { Button, Card, Drawer, FieldHint, Input, Pill, Segmented, Tag, useToast, type Tone } from '@/design-system';
import { cx } from '@/lib/cx';
import { formatNumber } from '@/lib/format';
import { errorField, errorText } from '@/shell/errorText';
import { LoadError } from '@/shell/LoadError';
import { connectorsQuery, patchConnector } from './api';
import { SecretField } from './SecretField';
import s from './settings.module.css';

type Tv = (k: string, o?: Record<string, unknown>) => string;

/** The connector's readiness, from booleans only (CONNECTORS §4: readiness without revealing configuration). */
export function connectorState(c: ConnectorOut): 'ready' | 'needs_keys' | 'off' {
  if (!c.enabled) return 'off';
  return c.health.configured ? 'ready' : 'needs_keys';
}
const STATE_TONE: Record<ReturnType<typeof connectorState>, Tone> = { ready: 'ok', needs_keys: 'warn', off: 'mute' };

/** The API's bounds (connectors/service.py `_LIMIT_KEYS`); max_bytes is edited in MB. */
const LIMITS: { key: keyof ConnectorLimits; min: number; max: number }[] = [
  { key: 'requests_per_minute', min: 1, max: 600 },
  { key: 'max_documents', min: 1, max: 5000 },
  { key: 'max_bytes', min: 1, max: 500 },
  { key: 'min_interval', min: 0, max: 60 },
];
const MB = 1024 * 1024;
const shown = (key: keyof ConnectorLimits, v: number) => (key === 'max_bytes' ? Math.round((v / MB) * 10) / 10 : v);
const fmtLimit = (key: keyof ConnectorLimits, v: number) => {
  const x = shown(key, v);
  return formatNumber(x, Number.isInteger(x) ? 0 : 1);
};

/** «Источники Research» (P3-E04, additive; DESIGN_SPEC §9 C7): one card per research connector. */
export function ResearchSources() {
  const { t } = useTranslation('settings');
  const tv = t as unknown as Tv;
  const q = useQuery(connectorsQuery);
  const [editing, setEditing] = useState<string | null>(null);
  if (q.isError) return <LoadError error={q.error} onRetry={() => void q.refetch()} />;
  if (!q.data) return null;
  const current = q.data.find((c) => c.kind === editing);
  return (
    <>
      <div className={s.note}>{t('sources.intro')}</div>
      <div className={s.grid280}>
        {q.data.map((c) => {
          const state = connectorState(c);
          const creds = Object.entries(c.credentials);
          return (
            <Card key={c.kind} className={s.connector} data-testid={`connector-${c.kind}`}>
              <div className={s.rowBetween}>
                <span className={s.name17}>{tv(`sources.kinds.${c.kind}`, { defaultValue: c.kind })}</span>
                <Pill tone={STATE_TONE[state]} size="tagTight">
                  {t(`sources.state.${state}`)}
                </Pill>
              </div>
              <dl className={s.kv}>
                {LIMITS.map((l) => (
                  <div key={l.key} className={s.kvRow}>
                    <dt className={s.kvKey}>{t(`sources.limits.${l.key}`)}</dt>
                    <dd className={s.kvValue}>{t(`sources.limit_values.${l.key}`, { value: fmtLimit(l.key, c.limits[l.key]) })}</dd>
                  </div>
                ))}
                {creds.map(([name, st]) => (
                  <div key={name} className={s.kvRow}>
                    <dt className={s.kvKey}>{tv(`sources.credentials.${name}`, { defaultValue: name })}</dt>
                    {/* Write-only: the server only ever says {set, masked}. */}
                    <dd className={cx(s.kvValue, st.set && s.mono)}>{st.set ? (st.masked ?? '••••') : t('sources.not_set')}</dd>
                  </div>
                ))}
              </dl>
              <div className={s.chips}>
                {c.health.capabilities.map((cap) => (
                  <Tag key={cap} soft>
                    {tv(`sources.capabilities.${cap}`, { defaultValue: cap })}
                  </Tag>
                ))}
              </div>
              <span className={s.text125}>{tv(`sources.notes.${c.kind}`, { defaultValue: c.health.detail ?? '' })}</span>
              <Button variant="secondary" size={34} icon="pencil" iconSize={14} className={cx(s.alignStart, s.pushDown)} onClick={() => setEditing(c.kind)}>
                {t('sources.edit')}
              </Button>
            </Card>
          );
        })}
      </div>
      {current && <ConnectorDrawer connector={current} onClose={() => setEditing(null)} />}
    </>
  );
}

/** Enable, limits and write-only credentials of one connector: PATCH sends only what changed. */
export function ConnectorDrawer({ connector, onClose }: { connector: ConnectorOut; onClose: () => void }) {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const tv = t as unknown as Tv;
  const toast = useToast();
  const qc = useQueryClient();
  const c = connector;
  const name = tv(`sources.kinds.${c.kind}`, { defaultValue: c.kind });
  const [enabled, setEnabled] = useState(c.enabled);
  const [limits, setLimits] = useState<Record<string, string>>(() => Object.fromEntries(LIMITS.map((l) => [l.key, String(shown(l.key, c.limits[l.key]))])));
  const names = Object.keys(c.credentials);
  const [typed, setTyped] = useState<Record<string, string>>(() => Object.fromEntries(names.map((n) => [n, ''])));
  const [clearing, setClearing] = useState<Record<string, boolean>>({});
  const [problem, setProblem] = useState<{ text: string; field: string | null } | null>(null);

  const save = useMutation({
    mutationFn: () => {
      const body: ConnectorPatch = {};
      if (enabled !== c.enabled) body.enabled = enabled;
      const changed: Record<string, number> = {};
      for (const l of LIMITS) {
        const raw = Number(String(limits[l.key] ?? '').replace(',', '.'));
        const value = l.key === 'max_bytes' ? Math.round(raw * MB) : raw;
        if (value !== c.limits[l.key]) changed[l.key] = value;
      }
      if (Object.keys(changed).length) body.limits = changed;
      const creds: Record<string, string | null> = {};
      for (const n of names) {
        if ((typed[n] ?? '').trim()) creds[n] = (typed[n] as string).trim();
        else if (clearing[n]) creds[n] = null;
      }
      if (Object.keys(creds).length) body.credentials = creds;
      return patchConnector(c.kind, body);
    },
    onSuccess: (next) => {
      // The response carries only {set, masked}: the typed values go with the form.
      setTyped(Object.fromEntries(names.map((n) => [n, ''])));
      qc.setQueryData<ConnectorOut[]>(qk.settings('connectors'), (list) => list?.map((x) => (x.kind === next.kind ? next : x)));
      toast(t('sources.saved', { name }));
      onClose();
    },
    onError: (err) => {
      const field = errorField(err);
      const limit = field?.startsWith('limits.') ? field.slice(7) : null;
      setProblem({ text: limit ? t('sources.limit_invalid', { name: tv(`sources.limits.${limit}`, { defaultValue: limit }) }) : errorText(err, tc), field });
    },
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setProblem(null);
    save.mutate();
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={name}
      sub={t('sources.drawer_sub')}
      closeLabel={tc('actions.close_esc')}
      footerNote={
        problem && (
          <span className={s.problem} role="alert">
            {problem.text}
          </span>
        )
      }
      footer={
        <div className={s.drawerActions}>
          <Button size={38} variant="secondary" onClick={onClose}>
            {tc('actions.cancel')}
          </Button>
          <Button size={38} type="submit" form="connector-settings" disabled={save.isPending}>
            {t('sources.save')}
          </Button>
        </div>
      }
    >
      <form id="connector-settings" className={s.drawerForm} onSubmit={submit} noValidate>
        <div className={s.group}>
          <span className={s.groupLabel}>{t('sources.enabled')}</span>
          <Segmented
            variant="form"
            label={t('sources.enabled')}
            value={enabled ? 'on' : 'off'}
            onChange={(v) => setEnabled(v === 'on')}
            className={s.alignStart}
            options={[
              { value: 'on', label: t('sources.enabled_on') },
              { value: 'off', label: t('sources.enabled_off') },
            ]}
          />
          <FieldHint>{t('sources.enabled_hint')}</FieldHint>
        </div>
        <div className={s.grid2}>
          {LIMITS.map((l) => (
            <div key={l.key} className={s.group}>
              <Input
                label={t(`sources.limits_edit.${l.key}`)}
                inputMode="decimal"
                value={limits[l.key] ?? ''}
                aria-invalid={problem?.field === `limits.${l.key}` || undefined}
                onChange={(e) => setLimits((x) => ({ ...x, [l.key]: e.target.value }))}
              />
              <FieldHint>{t('sources.range', { min: l.min, max: l.max })}</FieldHint>
            </div>
          ))}
        </div>
        {names.map((n) => (
          <SecretField
            key={n}
            testId={`credential-${n}`}
            labels={{
              label: tv(`sources.credentials.${n}`, { defaultValue: n }),
              set: (masked) => t('ai_edit.key_set', { masked }),
              unset: t('ai_edit.key_unset'),
              replace: t('sources.secret_new', { name: tv(`sources.credentials.${n}`, { defaultValue: n }) }),
              clear: t('sources.secret_clear'),
              willClear: t('sources.secret_will_clear'),
              hint: tv(`sources.credential_hints.${n}`, { defaultValue: '' }) || undefined,
            }}
            state={c.credentials[n]}
            value={typed[n] ?? ''}
            clearing={!!clearing[n]}
            onType={(value) => {
              setTyped((x) => ({ ...x, [n]: value }));
              setClearing((x) => ({ ...x, [n]: false }));
            }}
            onClear={() => setClearing((x) => ({ ...x, [n]: !x[n] }))}
          />
        ))}
      </form>
    </Drawer>
  );
}
